import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Plus, Send, Trash2, X as XIcon, Pencil, BarChart2, ArrowUpRight, LineChart } from 'lucide-react';
import { getForms, deleteForm, closeForm } from '../../api';
import FormBuilder from './FormBuilder';
import SendFormModal from './SendFormModal';
import FormResponses from './FormResponses';
import RPEAnalysis from './RPEAnalysis';

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };

const STATUS_STYLE = {
  draft:  { label: 'Draft',  color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  sent:   { label: 'Sent',   color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  closed: { label: 'Closed', color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
};

// Wellness & RPE are always-open daily check-ins backed by their own
// dedicated tables/dashboards (Team Overview, Session RPE, notifications) —
// not part of the generic forms/form_questions model. These two rows just
// relocate access into the Forms list per the user's request; they aren't
// editable/sendable/closable like a real form.
// RPE split into two independent daily forms on 2026-08-17: Pitch (muscular + cardiovascular)
// and Gym (muscular only). Both write to the same `rpe` table, separated by session_type.
const BUILT_IN_FORMS = [
  { key: 'wellness',  title: 'Wellness Check-in', subtitle: 'Daily sleep, energy, soreness & stress check-in' },
  { key: 'rpe-pitch', title: 'Pitch RPE',         subtitle: 'Muscular + cardiovascular rating for pitch sessions' },
  { key: 'rpe-gym',   title: 'Gym RPE',           subtitle: 'Muscular rating for gym sessions' },
];

function fmtDeadline(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function Forms({ user, onNavigate }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderFor, setBuilderFor] = useState(null); // null | 'new' | form id (draft to edit)
  const [sendingFor, setSendingFor] = useState(null); // form object
  const [viewingResponsesFor, setViewingResponsesFor] = useState(null); // form id
  const [viewingAnalysis, setViewingAnalysis] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getForms().then(setForms).catch(() => setForms([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (form) => {
    if (!window.confirm(`Delete draft "${form.title}"? This cannot be undone.`)) return;
    await deleteForm(form.id);
    load();
  };

  const handleClose = async (form) => {
    if (!window.confirm(`Close "${form.title}"? Players who haven't submitted will no longer see it.`)) return;
    await closeForm(form.id);
    load();
  };

  if (viewingAnalysis) {
    return <RPEAnalysis onBack={() => setViewingAnalysis(false)} />;
  }

  if (viewingResponsesFor) {
    return <FormResponses formId={viewingResponsesFor} onBack={() => { setViewingResponsesFor(null); load(); }} />;
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <ClipboardList size={22} /> Forms
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Build questionnaires and send them to players.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setViewingAnalysis(true)}
            style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}>
            <LineChart size={15} /> Analysis
          </button>
          <button onClick={() => setBuilderFor('new')}
            style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}>
            <Plus size={15} /> New form
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dimmed)' }}>Loading…</div>
      ) : (
        <div style={{ ...card, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Title', 'Status', 'Deadline', 'Completion', ''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)', background: 'var(--bg-app)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BUILT_IN_FORMS.map(bf => (
                <tr key={bf.key} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onClick={() => {
                    if (bf.key.startsWith('rpe')) setViewingAnalysis(true);
                    else if (onNavigate) onNavigate('wellness');
                  }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{bf.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-dimmed)', marginTop: 2 }}>{bf.subtitle}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', background: 'rgba(167,139,250,0.12)', padding: '3px 10px', borderRadius: 99 }}>Built-in</span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>Daily</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>—</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                      View <ArrowUpRight size={13} />
                    </span>
                  </td>
                </tr>
              ))}
              {!forms.length && (
                <tr>
                  <td colSpan={5} style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-dimmed)', fontStyle: 'italic' }}>
                    No custom forms yet — create one above.
                  </td>
                </tr>
              )}
              {forms.map(f => {
                const st = STATUS_STYLE[f.status] || STATUS_STYLE.draft;
                const total = Number(f.total) || 0;
                const submitted = Number(f.submitted) || 0;
                const overdue = Number(f.overdue) || 0;
                return (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--border)', cursor: f.status !== 'draft' ? 'pointer' : 'default' }}
                    onClick={() => { if (f.status !== 'draft') setViewingResponsesFor(f.id); }}>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{f.title}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: st.color, background: st.bg, padding: '3px 10px', borderRadius: 99 }}>{st.label}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDeadline(f.deadline)}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {f.status === 'draft' ? '—' : (
                        <span>
                          {submitted}/{total} submitted
                          {overdue > 0 && <span style={{ marginLeft: 8, color: '#ef4444', fontWeight: 700 }}>{overdue} overdue</span>}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {f.status === 'draft' && (
                          <>
                            <button onClick={() => setBuilderFor(f.id)} title="Edit"
                              style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 7, color: 'var(--text-secondary)', cursor: 'pointer', padding: '5px 8px', display: 'flex' }}><Pencil size={13} /></button>
                            <button onClick={() => setSendingFor(f)} title="Send"
                              style={{ background: '#2563eb', border: 'none', borderRadius: 7, color: '#fff', cursor: 'pointer', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}><Send size={13} /> Send</button>
                            <button onClick={() => handleDelete(f)} title="Delete"
                              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 5, display: 'flex' }}><Trash2 size={14} /></button>
                          </>
                        )}
                        {f.status === 'sent' && (
                          <>
                            <button onClick={() => setViewingResponsesFor(f.id)} title="Responses"
                              style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 7, color: 'var(--text-secondary)', cursor: 'pointer', padding: '5px 8px', display: 'flex' }}><BarChart2 size={13} /></button>
                            <button onClick={() => handleClose(f)} title="Close"
                              style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 7, color: 'var(--text-muted)', cursor: 'pointer', padding: '5px 8px', display: 'flex' }}><XIcon size={13} /></button>
                          </>
                        )}
                        {f.status === 'closed' && (
                          <button onClick={() => setViewingResponsesFor(f.id)} title="Responses"
                            style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 7, color: 'var(--text-secondary)', cursor: 'pointer', padding: '5px 8px', display: 'flex' }}><BarChart2 size={13} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {builderFor && (
        <FormBuilder
          formId={builderFor === 'new' ? null : builderFor}
          user={user}
          onClose={() => setBuilderFor(null)}
          onSaved={() => { setBuilderFor(null); load(); }}
        />
      )}

      {sendingFor && (
        <SendFormModal
          form={sendingFor}
          onClose={() => setSendingFor(null)}
          onSent={() => { setSendingFor(null); load(); }}
        />
      )}
    </div>
  );
}
